/**
 * KPI Detail Modal Component
 * KPIカードクリック時の詳細分析モーダル
 */

import { Modal, Stack, Group, Text, Badge, SimpleGrid, Paper, Center, Loader, Skeleton } from '@mantine/core';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { IconTrendingUp, IconTrendingDown } from '@tabler/icons-react';
import type { KpiDetailData } from '../types/kpiDetail';

type KpiDetailModalProps = {
  opened: boolean;
  onClose: () => void;
  data: KpiDetailData | null;
  isLoading?: boolean;
};

const COLORS = ['#3b82f6', '#14b8a6', '#8b5cf6', '#22d3ee', '#22c55e', '#f59e0b', '#ef4444'];

export const KpiDetailModal = ({ opened, onClose, data, isLoading = false }: KpiDetailModalProps) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={
        <Group>
          <Text fw={700} size="lg">
            {data?.label ?? 'KPI詳細'} - 詳細分析
          </Text>
          {data && (
            <Badge size="lg" color="blue">
              {data.current_value.toLocaleString()}
            </Badge>
          )}
        </Group>
      }
      overlayProps={{
        opacity: 0.5,
        blur: 16,
      }}
    >
      {isLoading ? (
        <Stack gap="md">
          {/* ローディング中のスケルトン表示 */}
          <SimpleGrid cols={4}>
            {[1, 2, 3, 4].map((i) => (
              <Paper key={i} className="glass" p="md">
                <Skeleton height={12} width="40%" mb="xs" />
                <Skeleton height={24} width="60%" />
              </Paper>
            ))}
          </SimpleGrid>
          <Paper className="glass" p="md">
            <Skeleton height={16} width="30%" mb="xs" />
            <Center h={200}>
              <Loader size="lg" />
            </Center>
          </Paper>
          <Paper className="glass" p="md">
            <Skeleton height={16} width="20%" mb="xs" />
            <Group align="flex-start">
              <Skeleton height={200} width="50%" />
              <Stack gap="xs" style={{ flex: 1 }}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} height={20} />
                ))}
              </Stack>
            </Group>
          </Paper>
        </Stack>
      ) : !data ? (
        <Center h={200}>
          <Text c="dimmed">データがありません</Text>
        </Center>
      ) : (
        <Stack gap="md">
          {/* 比較カード */}
          <SimpleGrid cols={4}>
            {[
              { label: '今日', value: data.comparison.today, compare: data.comparison.yesterday },
              { label: '昨日', value: data.comparison.yesterday, compare: data.comparison.last_week },
              { label: '先週', value: data.comparison.last_week, compare: data.comparison.last_month },
              { label: '先月', value: data.comparison.last_month, compare: 0 },
            ].map((item, i) => {
              const diff = item.compare > 0 ? item.value - item.compare : 0;
              const trend = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
              return (
                <Paper key={i} className="glass" p="md">
                  <Text size="xs" c="dimmed">
                    {item.label}
                  </Text>
                  <Group gap={4}>
                    <Text size="lg" fw={700}>
                      {item.value.toLocaleString()}
                    </Text>
                    {diff !== 0 && (
                      <Badge size="xs" color={trend === 'up' ? 'green' : 'red'}>
                        {trend === 'up' ? <IconTrendingUp size={10} /> : <IconTrendingDown size={10} />}
                        {Math.abs(diff)}
                      </Badge>
                    )}
                  </Group>
                </Paper>
              );
            })}
          </SimpleGrid>

          {/* 30日間トレンドグラフ */}
          <Paper className="glass" p="md">
            <Text size="sm" fw={600} mb="xs">
              過去30日間の推移
            </Text>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={data.timeline}>
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    borderRadius: '4px',
                  }}
                  formatter={(value: number) => [value.toLocaleString(), '値']}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 6 }}
                  animationDuration={1000}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>

          {/* 内訳円グラフ */}
          {data.breakdown.length > 0 && (
            <Paper className="glass" p="md">
              <Text size="sm" fw={600} mb="xs">
                内訳
              </Text>
              <Group align="flex-start">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie
                      data={data.breakdown}
                      dataKey="value"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ label, percentage }) => `${label}: ${percentage}%`}
                      labelLine={false}
                    >
                      {data.breakdown.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => value.toLocaleString()} />
                  </PieChart>
                </ResponsiveContainer>
                <Stack gap="xs" style={{ flex: 1 }}>
                  {data.breakdown.map((item, i) => (
                    <Group key={i} justify="space-between">
                      <Group gap="xs">
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: 2,
                            background: COLORS[i % COLORS.length],
                          }}
                        />
                        <Text size="sm">{item.label}</Text>
                      </Group>
                      <Group gap="xs">
                        <Text size="sm" fw={600}>
                          {item.value.toLocaleString()}
                        </Text>
                        <Badge size="xs" color="gray">
                          {item.percentage}%
                        </Badge>
                      </Group>
                    </Group>
                  ))}
                </Stack>
              </Group>
            </Paper>
          )}
        </Stack>
      )}
    </Modal>
  );
};
